import { useState, useReducer, useMemo, useCallback } from "react";
import { Button, Checkbox } from "@carbon/react";
import { AwardOrderRequest, OfferResponse, OrderResponse } from "../../types";
import i18next from "../../i18n";
import { currencyFormatter, offerLineTotals } from "../../utils";
import { formatTotals } from "../LinePriceTable/LinePriceTable";
import OfferAssistantPanel from "../OfferAssistant/OfferAssistantPanel";
import { useAwardRecommendation } from "../OfferAssistant/useAwardRecommendation";
import AwardAcceptDialog from "./AwardAcceptDialog";
import AwardMatrix from "./AwardMatrix";
import AwardOfferDetail from "./AwardOfferDetail";
import AwardWarnings from "./AwardWarnings";
import {
  awardedSelection,
  formatOffers,
  buildSingleAwardPayload,
} from "./awardFormat";
import { actions, createAwardReducer, initialEventState } from "./awardReducer";
import "./award.scss";
import "../AwardJoint/awardJoint.scss";

type AwardProps = {
  offers: OfferResponse[];
  onAcceptOffer: (offers: AwardOrderRequest[]) => void;
  onCancel: () => void;
  /** Retained for call-site compatibility; the panel is now closed by the parent's mutation
   * onSuccess rather than optimistically from here. */
  setIsAward?: () => void;
  order: OrderResponse;
  /** The award mutation is in flight — keeps the confirm dialog open with a loading button until
   * the parent closes the panel on success (so the buyer sees progress, not a silent close). */
  isAwarding?: boolean;
  /** Shows a closed award as it was decided: no edits, no award, no offer assistant. */
  readOnly?: boolean;
};

const Award = ({
  offers,
  onAcceptOffer,
  onCancel,
  order,
  isAwarding = false,
  readOnly = false,
}: AwardProps) => {
  const [openAccept, setOpenAccept] = useState(false);
  const [offerSelectionComment, setOfferSelectionComment] = useState("");
  const [offerSelectionFileUrl, setOfferSelectionFileUrl] = useState("");
  const [selectedOfferDetails, setSelectedOfferDetails] =
    useState<OfferResponse | null>(null);
  const [isSlideOpen, setIsSlideOpen] = useState(false);
  const [isBestSelected, setIsBestSelected] = useState(false);

  const formattedOffers = useMemo(() => formatOffers(offers), [offers]);

  const bestOffer = useMemo(
    () => formattedOffers.find((o) => o.isBest),
    [formattedOffers]
  );

  // One column per supplier (each with a single cell — the order's one material line), so the
  // single award uses the same suppliers-as-columns matrix as the joint award.
  const supplierGroups = useMemo(
    () =>
      formattedOffers.map((fo) => [
        { offer: fo, isBestOffer: fo.isBest, isBestAccumulate: fo.isBest },
      ]),
    [formattedOffers]
  );
  const bestTotal = bestOffer
    ? Number(bestOffer.price) * Number(bestOffer.order.quantity)
    : 0;
  const bestCurrency = bestOffer?.currency ?? undefined;
  // SCRUM-116: a line-priced service may be quoted in several currencies; show each.
  const bestText = offerLineTotals(bestOffer).length
    ? formatTotals(offerLineTotals(bestOffer))
    : currencyFormatter(bestTotal, bestCurrency);

  const [event, updateEvent] = useReducer(
    createAwardReducer(order, formattedOffers),
    initialEventState,
    (initial) =>
      readOnly
        ? { ...initial, offersToAccept: awardedSelection(formattedOffers) }
        : initial
  );

  // Offer assistant: a recommendation pre-selects offers (never awards) and marks the matrix.
  const preselect = useCallback((offerIds: string[]) => {
    updateEvent({ type: actions.APPLY_RECOMMENDATION, offerIds });
    setIsBestSelected(false);
  }, []);
  const aiRecommendation = useAwardRecommendation(preselect);

  const onAwardTender = () => {
    // Only fire the mutation. The parent closes this panel on success (setIsAward), so the confirm
    // dialog stays open showing its loading button meanwhile, and on error it stays open to retry.
    const formatted = buildSingleAwardPayload(
      event.offersToAccept,
      order,
      offerSelectionComment,
      offerSelectionFileUrl
    );
    onAcceptOffer(formatted);
  };

  // Toggle one supplier column in/out of the selection (several may be selected to split volume).
  const toggleOffer = (offer: (typeof formattedOffers)[number]) => {
    const isSelected = event.offersToAccept.some((s) => s.id === offer.id);
    if (isSelected) {
      updateEvent({ type: actions.DELETE_OFFER, id: offer.id });
    } else {
      updateEvent({
        type: actions.UPDATE_ON_CHECKBOX_CHANGE,
        id: offer.id,
        price: offer.price,
        volume: offer.order.quantity,
        orderId: offer.order.id,
        material: offer.order.material.globalMaterialName,
        offer,
      });
    }
  };

  const onToggleBest = () => {
    if (isBestSelected) {
      updateEvent({ type: actions.CLEAR_OFFERS, offersToAccept: [] });
    } else {
      updateEvent({ type: actions.SELECT_BEST_OFFERS });
    }
    setIsBestSelected(!isBestSelected);
  };

  const grandTotal = event.offersToAccept.reduce(
    (sum, o: any) => sum + (Number(o.volume) || 0) * (Number(o.price) || 0),
    0
  );
  const selectedNames = event.offersToAccept
    .map((o: any) => o.offer?.user?.company?.name)
    .filter(Boolean)
    .join(", ");
  const barCurrency =
    (event.offersToAccept[0] as any)?.offer?.currency ?? undefined;
  const selectedOffer = (event.offersToAccept[0] as any)?.offer;
  const selectionText =
    event.offersToAccept.length === 1 && offerLineTotals(selectedOffer).length > 1
      ? formatTotals(offerLineTotals(selectedOffer))
      : currencyFormatter(grandTotal, barCurrency);
  const canAward =
    event.offersToAccept.length > 0 && event.invalid.length === 0;

  return (
    <>
      {!readOnly && (
        <AwardAcceptDialog
          open={openAccept}
          comment={offerSelectionComment}
          onCommentChange={setOfferSelectionComment}
          fileUrl={offerSelectionFileUrl}
          onFileUrlChange={setOfferSelectionFileUrl}
          onConfirm={onAwardTender}
          onCancel={() => setOpenAccept(false)}
          loading={isAwarding}
        />
      )}

      {!readOnly && (
        <OfferAssistantPanel
          orderId={order.multipleOrder ? undefined : order.id}
          multipleOrderId={order.multipleOrder?.id}
          {...aiRecommendation.panelProps}
        />
      )}

      <div style={{ marginTop: "1rem" }}>
        <div className="award-joint-summary">
          <div className="award-joint-summary__card">
            {!readOnly && (
              <label className="award-joint-summary__toggle">
                <Checkbox
                  id="award-select-best"
                  labelText={i18next.t("award.selectBest")}
                  checked={isBestSelected}
                  onChange={onToggleBest}
                />
              </label>
            )}
            <div className="award-joint-summary__label">
              {i18next.t("award.bestPossibleTotal")}
            </div>
            <div className="award-joint-summary__value award-joint-summary__value--best">
              {bestText}
            </div>
          </div>

          <div className="award-joint-summary__card">
            <div className="award-joint-summary__label">
              {i18next.t(readOnly ? "award.awarded" : "award.yourSelection")}
            </div>
            <div className="award-joint-summary__value">
              {selectionText}
            </div>
          </div>
        </div>

        <AwardMatrix
          materials={[order]}
          supplierGroups={supplierGroups}
          offersToAccept={event.offersToAccept}
          errors={event.errors}
          aiMarks={readOnly ? null : aiRecommendation.marks}
          readOnly={readOnly}
          onSupplierToggle={(group) =>
            group.forEach((c: any) => toggleOffer(c.offer))
          }
          onCellToggle={(cell) => toggleOffer(cell.offer)}
          onVolumeChange={(cell, volume) =>
            updateEvent({
              type: actions.UPDATE_VOLUME,
              id: cell.offer.id,
              volume,
              orderId: cell.offer.order.id,
            })
          }
          onPriceChange={(cell, price) =>
            updateEvent({
              type: actions.UPDATE_PRICE,
              id: cell.offer.id,
              price,
              orderId: cell.offer.order.id,
            })
          }
          onViewDetail={(offer) => {
            setSelectedOfferDetails(offer);
            setIsSlideOpen(true);
          }}
        />
      </div>

      {!readOnly && (
        <AwardWarnings
          errors={event.errors}
          invalid={event.invalid}
          warningTitle={i18next.t("tenders.errors.title")}
        />
      )}

      <div className="award-bar">
        <div>
          <div className="award-bar__caption">
            {selectedNames
              ? i18next.t(readOnly ? "award.awardedTo" : "award.awardingTo")
              : i18next.t("award.noSupplierSelected")}
          </div>
          {selectedNames && (
            <div className="award-bar__summary">
              {selectedNames} · {selectionText}
            </div>
          )}
        </div>
        <div className="award-bar__actions">
          {readOnly ? (
            <Button kind="secondary" onClick={onCancel}>
              {i18next.t("award.backToRfq")}
            </Button>
          ) : (
            <>
              <Button
                kind="secondary"
                onClick={() => {
                  updateEvent({
                    type: actions.CLEAR_OFFERS,
                    offersToAccept: [],
                  });
                  onCancel();
                }}
              >
                {i18next.t("cta.cancel")}
              </Button>
              <Button onClick={() => setOpenAccept(true)} disabled={!canAward}>
                {i18next.t("tenders.data.accept")}
              </Button>
            </>
          )}
        </div>
      </div>

      <AwardOfferDetail
        open={isSlideOpen}
        onClose={() => setIsSlideOpen(false)}
        offer={selectedOfferDetails}
        dateFormatter={(d) => new Date(d).toLocaleDateString()}
      />
    </>
  );
};

export default Award;
